import { StateService, Transition } from '@uirouter/core';

import { concat, first, firstValueFrom, forkJoin, map, mergeMap } from 'rxjs';

import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

import { StateUtilsService } from '@app/_helpers/state-utils.service';
import { Abonne } from '@app/abonne/abonne.model';
import { AbonneService } from '@app/abonne/abonne.service';
import { gestionnaireAbonneMenuItems } from '@app/gestionnaire-abonnement-menu-items';
import {conformiteMenuItems} from '@app/conformite-menu-items'
import { NomenclatureService } from '@app/nomenclature/nomenclature.service';
import { SiteService } from '@app/site/site.service';
import { superAdminMenuItems } from '@app/super-admin-menu-items';
import { Utilisateur } from '@app/utilisateur/utilisateur.model';
import { UtilisateurService } from '@app/utilisateur/utilisateur.service';
import { AppMenuService } from '@global/app-menu.service';
import { ListesGeneriquesService } from './listes-generiques.service';
import { StorageService } from './storage.service';
import { TranslateService } from '@codeandweb/ngx-translate';
import { ProfilDialogComponent } from '@app/utilisateur/profil-dialog';

import { ExtensibleObject, isEmpty } from '@helpers/utils';
import { EventManagerService } from './event-manager.service';

export const getListesGeneriquesResolve = {
	token: 'listesGeneriques',
	deps: [StateService, ListesGeneriquesService],
	resolveFn: (stateService: StateService, listesGeneriquesService: ListesGeneriquesService) => {
		// console.log('getListesGeneriquesResolve');
		return listesGeneriquesService.getListesGeneriques().toPromise()
		.then(
			(listesGeneriques: any) => {
				return listesGeneriques;
			},
			(error: any) => {
				if (error.status && error.status != 401) {
					stateService.go('service_unavailable', {error: 'true'});
				}
			}
		);
	}
}

// empty the sidebar menu
export const resetMenuResolve = {
	token: 'resetMenu',
	deps: [AppMenuService, Transition],
	resolveFn: (appMenuService: AppMenuService, transition: Transition) => {
		// console.log('resetMenuResolve');
		appMenuService.setMenuItems([]);
		return true;
	},
};

export const getMeResolve = {
	token: 'me',
	deps: [StateService, UtilisateurService],
	resolveFn: (stateService: StateService, utilisateurService: UtilisateurService) => {
		// console.log('getMeResolve');
		return utilisateurService.getCurrentUtilisateur().toPromise()
			.then(
				(me: any) => {
					return me;
				},
				(error: any) => {
					if (error.status && error.status != 401) {
						stateService.go('service_unavailable', { error: 'true' });
					}
				},
			);
	},
};

export const getAbonneCourantResolve = {
	token: 'abonne_courant',
	deps: [AbonneService, Transition],
	resolveFn: (abonneService: AbonneService, transition: Transition) => {
		const curr_abo_id = transition.params().curr_abo_id;

		return abonneService.getAbonne(curr_abo_id)
		.pipe(map((abonne: Abonne) => {
			abonneService.currentAbonneValue = abonne;
			return abonne;
		})).toPromise();
	}
};

export const redirectAbonne = {
	token: 'redirectAbonne',
	deps: [StateService, StorageService, AbonneService, Transition, 'me'],
	resolveFn: (
		stateService: StateService,
		storageService: StorageService,
		abonneService: AbonneService,
		transition: Transition,
		me: Utilisateur
	) => {

		// cas particulier de l'admin, l'abonné "courant" (s'il était sélectionné) doit être chargé plus tôt, hors du "main"
		if (me.uti_administrateur) {
			const stored_abo = storageService.get('abo', null);
			if (stored_abo) {
				abonneService.currentAbonneValue = stored_abo;
				return abonneService.refreshCurrentAbonneData().toPromise();
			}
		}

		// si utilisateur "normal", on gère juste le cas du state "main"
		// si l'utilisateur n'a qu'un abonnement, on le redirige immédiatement sur celui-ci
		// le getAbonneCourantResolve se chargera du reste
		if (transition.to().name == 'main' && me.abonnes.length == 1) {
			return stateService.go('abonne-courant', {curr_abo_id: me.abonnes[0].abo_id});
		}

		return true;
	}
}

export const getUtilisateurResolve = {
	token: 'utilisateur',
	deps: [UtilisateurService, Transition],
	resolveFn: (utilisateurService: UtilisateurService, transition: Transition) => {
		const uti_id = transition.params().uti_id;
		return utilisateurService.getUtilisateur(uti_id).toPromise();
	}
};

export const getSiteResolve = {
	token: 'site',
	deps: [SiteService, Transition, 'curr_abo_id'],
	resolveFn: (siteService: SiteService, transition: Transition, curr_abo_id: number) => {
		const sit_id = transition.params().sit_id;
		return siteService.getSite(curr_abo_id, sit_id).toPromise();
	}
};

export const getSitesResolve = {
	token: 'sites',
	deps: [SiteService, Transition, 'curr_abo_id'],
	resolveFn: (siteService: SiteService, transition: Transition, curr_abo_id: number) => {
		const sit_ids = transition.params().sit_ids as number[];
		const observables = sit_ids.map(sit_id => siteService.getCacheSite(curr_abo_id, sit_id));

		return forkJoin(observables).toPromise();
	}
};

export const getTexteResolve = {
	token: 'texte',
	deps: [NomenclatureService, Transition],
	resolveFn: (nomenclatureService: NomenclatureService, transition: Transition) => {
		const txt_id = transition.params().txt_id;
		return nomenclatureService.getTexte(txt_id).toPromise();
	}
};

export const initializeAppMenuResolve = {
	token: 'initializeAppMenu',
	deps: [
		AppMenuService,
		AbonneService,
		UtilisateurService,
		TranslateService,
		DialogService,
		EventManagerService,
		Transition,
		'me'
	],
	resolveFn: (
		appMenuService: AppMenuService,
		abonneService: AbonneService,
		utilisateurService: UtilisateurService,
		translate: TranslateService,
		dialogService: DialogService,
		eventManager: EventManagerService,
		_: Transition,
		me: Utilisateur
	) => {

		const setMenuItems = (abonne: Abonne | null) => {

			let profileMenuItems: MenuItem = {
				label: `${me.uti_prenom} ${me.uti_nom.toLocaleUpperCase()} (${me.uti_email})`,
				icon: 'pi pi-user',
				items: [
					// Mon profil
					{
						label: translate.instant('user-menu.my-profile'),
						icon: 'pi pi-user-edit',
						command: () => {
							showProfileDialog(dialogService, utilisateurService, translate);
						}
					},

					// Se déconnecter
					{
						label: translate.instant('user-menu.disconnect'),
						icon: 'pi pi-sign-out',
						command: () => {
							eventManager.emit('logout', true);
						}
					}
				]
			};

			if (abonne == null) {
				if (me.uti_administrateur) {
					let menuItems: MenuItem[] = [
						{
							labelKey: 'menu.administration',
							icon: 'pi pi-cog',
							items: superAdminMenuItems
						},
						profileMenuItems
					];
					appMenuService.setMenuItems(menuItems);
				}
				else {
					appMenuService.setMenuItems([]);
				}

				return;
			}

			let menuItems: MenuItem[] = [];
			let utilisateurAbonne = abonne.utilisateurs_abonnes.find(utilisateur => utilisateur.uti_id == me.uti_id);
			let _gestionnaireAbonneMenuItems = withUiParamsInMenuItems(gestionnaireAbonneMenuItems, {curr_abo_id: abonne.abo_id});
			let _conformiteMenuItems = withUiParamsInMenuItems(conformiteMenuItems, {curr_abo_id: abonne.abo_id});

			let conformite = {
				labelKey: 'menu.compliance_management',
				icon: 'pi pi-file-check',
				items: _conformiteMenuItems
			};

			if (me.uti_administrateur) {
				menuItems.push(
					conformite,
					{
						labelKey: 'menu.abonne',
						icon: 'pi pi-building',
						items: _gestionnaireAbonneMenuItems
					},
					{
						labelKey: 'menu.administration',
						icon: 'pi pi-cog',
						items: superAdminMenuItems
					},
				);
			}
			else if (utilisateurAbonne != undefined && utilisateurAbonne.utb_gestion_abonnement) {
				menuItems.push(
					conformite,
					{
						labelKey: 'menu.abonne',
						icon: 'pi pi-building',
						items: _gestionnaireAbonneMenuItems
					},
				);
			} else  if (abonne.sites.length > 0){
				menuItems.push(conformite)
			}

			menuItems.push(profileMenuItems)

			appMenuService.setMenuItems(menuItems);
		};

		// Si les traductions sont chargées on souscrit immédiatement à abonneService.currentAbonne$
		if (translate.instant('user-menu.my-profile') != 'user-menu.my-profile') {
			abonneService.currentAbonne$.pipe(map(setMenuItems)).subscribe();
		}
		// Sinon on attend le premier chargement des traductions avant de souscrire à abonneService.currentAbonne$
		else {
			translate.onTranslationChange.pipe(first())
			.subscribe(() => {
				abonneService.currentAbonne$.pipe(map(setMenuItems)).subscribe();
			});
		}
	},
};

// function to determine where the user should be redirected
// after having been redirected to the login form
export const returnToResolve = {
	token: 'returnTo',
	deps: [StateService, StateUtilsService, Transition],
	resolveFn: (stateService: StateService, stateUtils: StateUtilsService, transition: Transition) => {
		// console.log('returnToResolve', transition.to().name, transition.from().name, transition.redirectedFrom());

		// The user was redirected to the login state (e.g., via the requiresAuth hook when trying to activate a state)
		if (transition.redirectedFrom() != null) {
			// Return to the original attempted target state (e.g., state.name)
			return transition.redirectedFrom().targetState();
		}

		let from = transition.from();

		// The user was not redirected to the login state; they directly activated the login state somehow.
		// Return them to the state they came from.
		if (from.name !== '' && isEmpty(transition.options().custom)) {
			return stateService.target(transition.from(), transition.params('from'));
		}

		// If the fromState's name is empty, then this was the initial transition. We'll try to return to the targeted url
		if (from.name == '') {
			return stateUtils.matchUrlToState(window.location.pathname);
		}

		return stateService.target('/');
	},
};



function withUiParamsInMenuItems(items: any[], params: ExtensibleObject) {
	items = structuredClone(items);
	for (let item of items) {
		if (item.state.startsWith('abonne-courant')) {
			item.uiParams = {curr_abo_id: params.curr_abo_id};
		}
	}

	return items;
}

export const getNomenclatureResolve = {
	token: 'nomenclature',
	deps: [NomenclatureService],
	resolveFn: (nomenclatureService: NomenclatureService) => {
		return nomenclatureService.getCacheNomenclature().toPromise();
	}
};

function showProfileDialog(dialogService: DialogService, utilisateurService: UtilisateurService, translate: TranslateService) {
	const ref = dialogService.open(ProfilDialogComponent, {
		header: translate.instant('user-menu.my-profile'),
		width: '70%',
	});
	ref.onClose.subscribe(dialogResult => {
		if (dialogResult && dialogResult.refreshCurrentUtilisateur == true) {
			utilisateurService.getCurrentUtilisateur()
				.subscribe();
		}
	});
}